import { useEffect } from 'react';
import { Topbar, Sidebar } from '../../components';
import { HugeiconsIcon } from '@hugeicons/react';
import { Settings01Icon, Sun01Icon, Moon02Icon, ComputerIcon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import { useTheme } from '../../hooks/useTheme';

const THEME_OPTIONS = [
    {
        value: 'light',
        label: 'Light',
        description: 'Always use the light theme',
        icon: Sun01Icon,
        iconBg: 'bg-amber-50 dark:bg-amber-500/15',
        iconColor: 'text-amber-500',
    },
    {
        value: 'dark',
        label: 'Dark',
        description: 'Always use the dark theme',
        icon: Moon02Icon,
        iconBg: 'bg-indigo-50 dark:bg-indigo-500/15',
        iconColor: 'text-indigo-500 dark:text-indigo-400',
    },
    {
        value: 'system',
        label: 'System',
        description: 'Follow your device preference',
        icon: ComputerIcon,
        iconBg: 'bg-gray-100 dark:bg-gray-700',
        iconColor: 'text-gray-500 dark:text-gray-400',
    },
];

function ThemePreview({ value }) {
    if (value === 'light') {
        return (
            <div className="w-full h-16 rounded-lg border border-gray-200 overflow-hidden flex">
                <div className="w-8 bg-[#1e3a6e] h-full" />
                <div className="flex-1 bg-white p-2 space-y-1">
                    <div className="h-1.5 w-3/4 bg-gray-200 rounded-full" />
                    <div className="h-1.5 w-1/2 bg-gray-200 rounded-full" />
                    <div className="h-4 w-full bg-gray-100 rounded mt-1" />
                </div>
            </div>
        );
    }
    if (value === 'dark') {
        return (
            <div className="w-full h-16 rounded-lg border border-gray-700 overflow-hidden flex">
                <div className="w-8 bg-[#1e3a6e] h-full" />
                <div className="flex-1 bg-gray-900 p-2 space-y-1">
                    <div className="h-1.5 w-3/4 bg-gray-700 rounded-full" />
                    <div className="h-1.5 w-1/2 bg-gray-700 rounded-full" />
                    <div className="h-4 w-full bg-gray-800 rounded mt-1" />
                </div>
            </div>
        );
    }
    return (
        <div className="w-full h-16 rounded-lg overflow-hidden flex border border-gray-200 dark:border-gray-700">
            <div className="w-8 bg-[#1e3a6e] h-full" />
            <div className="flex-1 flex">
                <div className="flex-1 bg-white p-2 space-y-1">
                    <div className="h-1.5 w-full bg-gray-200 rounded-full" />
                    <div className="h-1.5 w-2/3 bg-gray-200 rounded-full" />
                </div>
                <div className="flex-1 bg-gray-900 p-2 space-y-1">
                    <div className="h-1.5 w-full bg-gray-700 rounded-full" />
                    <div className="h-1.5 w-2/3 bg-gray-700 rounded-full" />
                </div>
            </div>
        </div>
    );
}

function Index() {
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        document.title = `Settings - ${process.env.REACT_APP_SITE_NAME}`;
    }, []);

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    {/* Header */}
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0">
                            <HugeiconsIcon icon={Settings01Icon} size={18} className="text-gray-600 dark:text-gray-300" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Settings</h1>
                            <p className="text-sm text-gray-400">Manage your portal preferences</p>
                        </div>
                    </div>

                    {/* Appearance card */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Appearance</h2>
                            <p className="text-xs text-gray-400 mt-0.5">Choose how the portal looks on your device</p>
                        </div>

                        <div className="p-5">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {THEME_OPTIONS.map((opt) => {
                                    const isActive = theme === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setTheme(opt.value)}
                                            className={`relative text-left rounded-xl border-2 p-4 transition-all focus:outline-none ${
                                                isActive
                                                    ? 'border-[#1e3a6e] dark:border-blue-400 bg-blue-50/60 dark:bg-blue-500/10 shadow-md'
                                                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                                            }`}
                                        >
                                            {/* Active checkmark */}
                                            <span className={`absolute top-3 right-3 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0'}`}>
                                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} className="text-[#1e3a6e] dark:text-blue-400" />
                                            </span>

                                            {/* Preview */}
                                            <div className="mb-3">
                                                <ThemePreview value={opt.value} />
                                            </div>

                                            {/* Icon + Label */}
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${opt.iconBg}`}>
                                                    <HugeiconsIcon icon={opt.icon} size={14} className={opt.iconColor} />
                                                </div>
                                                <span className={`text-sm font-semibold ${isActive ? 'text-[#1e3a6e] dark:text-blue-300' : 'text-gray-800 dark:text-gray-100'}`}>
                                                    {opt.label}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 pl-0.5">{opt.description}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                </section>
            </main>
        </div>
    );
}

export default Index;
